import { useFormContext, useFormField } from '@calendly/form'
import { useBooleanState, useOnValueChange } from '@calendly/hooks'
import { isEmpty, isEqual } from 'lodash-es'
import { useMemo } from 'react'

import { useDebounce } from 'hooks/useDebounce'

import { isBlankValue } from './utils'

export const useCalendarSettingsUrl = () => '/app/availability/connected_calendars'

export const useErrorAfterInitialTouch = (name, { isBlankFn = isBlankValue } = {}) => {
  const [{ value }, { error }, { setError }] = useFormField({ name })
  const [isInitiallyTouched, initialTouch] = useBooleanState(false)

  useOnValueChange(error, () => {
    if (isInitiallyTouched) return
    if (!isBlankFn(value)) return
    if (isBlankFn(error)) return

    setError(null)
    initialTouch()
  })
}

export const useDebouncedFormContext = (delay = 300) => {
  const { values, errors } = useFormContext()
  const memoizedFormContext = useMemo(() => ({ values, errors }), [values, errors])
  const debouncedFormContext = useDebounce(memoizedFormContext, delay)

  return debouncedFormContext
}

export const useHasFormErrors = () => {
  const { status, errors } = useFormContext()

  const hasFieldErrors = errors && Object.keys(errors).length > 0
  const hasFormError = !!status

  return hasFieldErrors || hasFormError
}

export const useDirtyState = (valuesMapper = (values) => values) => {
  const { values, initialValues } = useFormContext()

  return !isEqual(valuesMapper(values), initialValues) || isEmpty(initialValues)
}
